import './application-settings.css';

import { IonChip, IonIcon, IonToolbar } from '@ionic/react';
import { lockClosed } from 'ionicons/icons';
import { isEmpty, isNil } from 'lodash';
import { ReactElement, useCallback, useMemo, useState } from 'react';
import * as yup from 'yup';
import {
  usePermissions,
  usePermissionsLazy,
} from '../../../../../api/authorization.provider';
import {
  ScratchpadBucket,
  ScratchpadBucketScope,
  ScratchpadBucketType,
  useGetScratchpadBucketQuery,
} from '../../../../../api/graphql/generated';
import { useCurrentUserProp } from '../../../../../api/user.hooks';
import { FormSubmitButton } from '../../../../../components/form/form-submit.button';
import { SaveStatusToast } from '../../../../../components/toast/toast.gql';
import i18n from '../../../../../i18n';
import { getLocalizedText } from '../../../../../i18n/i18n.utils';
import { useMachineContext } from '../../../../../pages/machine/machine-context-provider';
import {
  ApplicationDefaultParameterSetFormikFormValues,
  useApplicationDefaultParameterSetService,
} from '../../hook/application-default-parameter-set.hooks';
import { useFlowServices } from '../../hook/flow-service.hooks';
import {
  ApplicationDefaultParameterSetBaseFormChild,
  ApplicationDefaultParameterSetBaseFormValidationBuilder,
  ApplicationDefaultParameterSetData,
  SystemUnit,
} from '../../model/flow.types';
import { useFlowRefContext } from '../flow/flow-ref';
import { PickerInput } from '../widget/picker-input';
import { TextInput } from '../widget/text-input';
import { PickerOption } from '../widget/widget.types';

export interface ApplicationSettingsBucketEditProps {
  sourceBucketId?: string;
  bucketId?: string;
  tags?: Record<string, unknown>;
  bucketData?: ApplicationDefaultParameterSetData;
  saveButtonText?: string;
  onSave: (bucket: ScratchpadBucket | undefined) => Promise<void>;
  tagsReadOnly?: boolean;
  applicationParametersReadOnly?: boolean;
  system: SystemUnit;
  useDefaultParams?: boolean;
  type?: ScratchpadBucketType;
}

const ApplicationSettingsBucketEdit = (
  props: ApplicationSettingsBucketEditProps
): ReactElement => {
  const {
    bucketId,
    onSave,
    bucketData,
    tagsReadOnly,
    applicationParametersReadOnly,
    saveButtonText,
    system,
    sourceBucketId,
    tags,
    useDefaultParams,
    type,
  } = props;

  const { flowService, flowItemService } = useFlowServices();
  const {
    componentRegistry: { ApplicationSettingsForm: ApplicationSettingsBaseForm },
  } = useFlowRefContext();

  const { machine } = useMachineContext();
  const {
    create: [createApplicationSettings, { createError }],
    update: [updateApplicationSettings, { updateError }],
  } = useApplicationDefaultParameterSetService();

  const [saving, setSaving] = useState(false);

  const teamId = machine?.teamId;
  const tenantId = useCurrentUserProp<string>('tenantId');

  const isNew = useMemo(() => {
    return isNil(bucketId);
  }, [bucketId]);

  const { data: scratchpadBucketData } = useGetScratchpadBucketQuery({
    variables: {
      input: {
        id: bucketId as string,
      },
    },
    skip: isNew,
  });

  const scratchpadBucket = useMemo<
    Partial<ScratchpadBucket> | undefined
  >(() => {
    if (!isNew && scratchpadBucketData) {
      return {
        ...scratchpadBucketData.getScratchpadBucket,
        data: bucketData ?? scratchpadBucketData.getScratchpadBucket.data,
      };
    } else if (isNew && bucketData) {
      return {
        data: bucketData,
        tags,
      };
    }
  }, [bucketData, isNew, scratchpadBucketData, tags]);

  const permissionCheck = usePermissionsLazy();

  const bucketPermissions = usePermissions(
    'scratchpad_bucket',
    scratchpadBucketData?.getScratchpadBucket
  );

  const isBucketReadonly = useMemo(() => {
    return bucketId ? !bucketPermissions.update : !bucketPermissions.create;
  }, [bucketId, bucketPermissions]);

  const formValues: ApplicationDefaultParameterSetFormikFormValues =
    useMemo(() => {
      return {
        name: scratchpadBucket?.name,
        tags: scratchpadBucket?.tags ?? {},
        notes: scratchpadBucket?.notes ?? '',
        data: flowItemService.convertFlowItemToForm(
          {
            parameters: scratchpadBucket?.data?.parameters ?? [],
            ref: flowItemService.getDefaultItemRef(),
          },
          system,
          !useDefaultParams
        ),
        scope: scratchpadBucket?.scope ?? ScratchpadBucketScope.TEAM,
      };
    }, [
      flowItemService,
      scratchpadBucket?.data?.parameters,
      scratchpadBucket?.name,
      scratchpadBucket?.notes,
      scratchpadBucket?.tags,
      scratchpadBucket?.scope,
      useDefaultParams,
      system,
    ]);

  const parameterSetTags = useMemo(() => {
    const equipment = flowService.getEquipmentName();

    if (equipment) {
      const mapping =
        flowService.getApplicationDefaultParameterSetTagToEquipmentMapping();

      return mapping[equipment] ?? {};
    }

    return {};
  }, [flowService]);

  const buildValidationSchema: ApplicationDefaultParameterSetBaseFormValidationBuilder =
    (dataSchema) => {
      const tagsSchema: yup.AnySchema = Object.entries(parameterSetTags).reduce(
        (schema, [k, v]) => {
          let tagSchema: yup.AnySchema = yup.string();

          if (v.required) {
            tagSchema = tagSchema.required();
          }

          schema = schema.shape({
            [k]: tagSchema,
          });

          return schema;
        },
        yup.object().shape({})
      );

      const itemSchema = yup.object().shape({
        name: yup.string().required(),
        tags: tagsSchema,
        data: dataSchema ?? yup.object().required(),
      });

      return itemSchema;
    };

  const onSubmit = useCallback(
    async (form: ApplicationDefaultParameterSetFormikFormValues) => {
      setSaving(true);

      let result: ScratchpadBucket | undefined = undefined;

      const item = flowItemService.convertFormToFlowItem(form.data, system);

      const tags: Record<string, unknown> = {
        ...form.tags,
        ...flowService.getApplicationDefaultParameterSetImplicitTags(item),
      };

      const notes = form.notes;

      const searchTagsArr = Object.keys(form.tags).reduce(
        (curr: string[], key: string) => {
          const value = form.tags[key];
          const metadata = parameterSetTags[key];
          if (metadata) {
            const selectedValueOptions = metadata.options.find(
              (o) => o.value === value
            );

            return curr.concat(
              selectedValueOptions?.searchTerms?.map((s) => s.toLowerCase()) ??
                []
            );
          }

          return curr;
        },
        []
      );

      if (searchTagsArr?.length > 0) {
        tags['$synonyms'] = searchTagsArr;
      }

      const parentId =
        form.scope === ScratchpadBucketScope.TENANT ? tenantId : teamId;

      if (isNew) {
        if (teamId !== undefined) {
          const createResult = await createApplicationSettings({
            name: form.name,
            data: {
              parameters: item.parameters,
            },
            parentId,
            scope: form.scope,
            sourceId: sourceBucketId,
            tags,
            notes,
            type,
          });

          result = createResult.data?.createScratchpadBucket;
        }
      } else {
        if (scratchpadBucket?.id) {
          const updateResult = await updateApplicationSettings({
            id: scratchpadBucket.id,
            name: form.name,
            data: {
              parameters: item.parameters,
            },
            tags,
            notes,
          });
          result = updateResult.data?.updateScratchpadBucket;
        }
      }

      setSaving(false);

      if (result && onSave) {
        await onSave(result);
      }
    },
    [
      flowItemService,
      system,
      flowService,
      isNew,
      onSave,
      teamId,
      createApplicationSettings,
      sourceBucketId,
      scratchpadBucket,
      updateApplicationSettings,
      tenantId,
      parameterSetTags,
    ]
  );

  const footer: ApplicationDefaultParameterSetBaseFormChild<ApplicationDefaultParameterSetFormikFormValues> =
    useCallback(
      ({ formikProps }) => {
        const { errors } = formikProps;

        const isReadyToSubmit = isEmpty(errors);
        return (
          <IonToolbar>
            {!isBucketReadonly && (
              <FormSubmitButton
                loading={saving}
                buttonProps={{
                  color: isReadyToSubmit ? 'primary' : 'medium',
                  disabled: !isReadyToSubmit || saving || isBucketReadonly,
                  fill: isReadyToSubmit ? 'solid' : 'outline',
                  onClick: formikProps.submitForm,
                }}
              >
                {saveButtonText ?? getLocalizedText('common:save')}
              </FormSubmitButton>
            )}
          </IonToolbar>
        );
      },
      [isBucketReadonly, saveButtonText, saving]
    );

  const bucketScopeOptions = useMemo<PickerOption[]>(() => {
    const options: PickerOption[] = [];

    const teamScopeEnabled = permissionCheck<Partial<ScratchpadBucket>>(
      'scratchpad_bucket',
      {
        parentId: teamId,
        scope: ScratchpadBucketScope.TEAM,
      }
    );

    const tenantScopeEnabled = permissionCheck<Partial<ScratchpadBucket>>(
      'scratchpad_bucket',
      {
        parentId: tenantId,
        scope: ScratchpadBucketScope.TENANT,
      }
    );

    if (teamScopeEnabled.create) {
      options.push({
        label: i18n.t('common:team'),
        value: ScratchpadBucketScope.TEAM,
      });
    }

    if (tenantScopeEnabled.create) {
      options.push({
        label: i18n.t('common:workspace'),
        value: ScratchpadBucketScope.TENANT,
      });
    }

    return options;
  }, [teamId, tenantId, permissionCheck]);

  const preForm: ApplicationDefaultParameterSetBaseFormChild<ApplicationDefaultParameterSetFormikFormValues> =
    useCallback(
      ({ formikProps: { values } }) => {
        return (
          <>
            {isBucketReadonly && (
              <div className="readonly-container">
                <IonChip color="danger">
                  {getLocalizedText('common:readonly_mode')}
                  <IonIcon icon={lockClosed} />
                </IonChip>
              </div>
            )}
            <TextInput
              name="name"
              value={values.name}
              label={getLocalizedText('common:name')}
              disabled={isBucketReadonly}
              placeholder={getLocalizedText(
                'common:application_settings_writer_name_placeholder'
              )}
            />
            {bucketScopeOptions?.length > 1 && (
              <PickerInput
                name="scope"
                value={values.scope}
                options={bucketScopeOptions}
                disabled={isBucketReadonly}
                label={i18n.t('common:scratchpad_bucket_scope')}
              />
            )}
          </>
        );
      },
      [bucketScopeOptions, isBucketReadonly]
    );

  return (
    <>
      {formValues && (
        <ApplicationSettingsBaseForm
          buildValidationSchema={buildValidationSchema}
          footer={footer}
          formValues={formValues}
          onSubmit={onSubmit}
          system={system}
          preForm={preForm}
          tagsReadOnly={tagsReadOnly}
          paramsReadOnly={applicationParametersReadOnly}
        />
      )}
      <SaveStatusToast loading={saving} error={createError || updateError} />
    </>
  );
};

export { ApplicationSettingsBucketEdit };
